<template>
  <div v-show="!uploading && !uploaded" class="flex flex-col justify-center items-center gap-4 grow px-5">
    <MainInstruction :isHybrid="isHybrid" />
    <div class="flex flex-col justify-center gap-5">
      <button v-if="!hasCameraPermission" @click="toggleCameraAccessModal" :disabled="cameraLoading"
        class="bg-[#00AEEF] px-5 py-2 text-black text-lg rounded-full font-bold uppercase">
        {{ cameraLoading ? $t('authentication.cameraLoading') : $t('authentication.grant') }}
      </button>
      <div v-else class="flex justify-center text-center">
        <label for="file-upload" @click="takePicture"
          class="bg-[#00AEEF] px-12 py-2 text-black text-lg text-center rounded-full font-bold uppercase">
          {{ $t('authentication.open') }}
        </label>
        <input class="hidden" id="file-upload" ref="fileInput" type="file" v-on:change="onFileChange"
          accept="image/jpg, image/jpeg" capture="environment" />
      </div>
      <Button buttonClass="underline text-sm font-bold text-[#00AEEF]" buttonName="instruction.button"
        @click="toggleInstruction()" />
    </div>
  </div>
  <PreloadScan v-if="uploading" :photo="previewSrc" />

  <ResponseResult
    v-if="uploaded"
    :isHybrid="isHybrid"
    :status="authStatus"
    :imageQuality="imageQuality"
    :photo="previewSrc"
    :decodedData="decodedData"
  />

  <Instructions :openInstruction="openInstructions" :isHybrid="isHybrid" @close="toggleInstruction" />

  <CameraAccessModal :visible="isCameraAccessVisible" />
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import * as amplitude from '@amplitude/analytics-browser'
import * as Sentry from "@sentry/vue"

import { events } from '@/utils/events'
import authenticationService from '@/utils/authentication'
import getLocation from '@/utils/getLocation'

import Button from './Button.vue'
import Instructions from './Instructions.vue'
import MainInstruction from './MainInstruction.vue'
import PreloadScan from './PreloadScan.vue'
import ResponseResult from './ResponseResult.vue'
import CameraAccessModal from './CameraAccessModal.vue'

const props = defineProps({
  decodedData: Object,
  isStc: Boolean,
  isHybrid: Boolean
})

const uploading = ref(false)
const uploaded = ref(false)
const authStatus = ref('')
const imageQuality = ref([])
const previewSrc = ref('#')
const fileInput = ref(null)
const openInstructions = ref(false)

const isCameraAccessVisible = ref(false)
const cameraLoading = ref(false)
const hasCameraPermission = ref(false)

onMounted(async () => {
  if(props.decodedData?.isDataFromMobileApp){
    isCameraAccessVisible.value = false
    hasCameraPermission.value = true
  } else {
    await isCameraPermissionGranted()
  }
  events.value.locationData = await getLocation(props.decodedData)
})

const isCameraPermissionGranted = async () => {
  try {
    cameraLoading.value = true
    const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
    stream.getVideoTracks().forEach(function (track) {
      track.stop()
    })
    cameraLoading.value = false
    hasCameraPermission.value = true
    // eslint-disable-next-line no-unused-vars
  } catch (error) {
    toggleCameraAccessModal()
    cameraLoading.value = false
    hasCameraPermission.value = false
  }
}

const toggleInstruction = () => {
  openInstructions.value = !openInstructions.value
}

const toggleCameraAccessModal = () => {
  isCameraAccessVisible.value = !isCameraAccessVisible.value

  if (!isCameraAccessVisible.value) {
    amplitude.track('Close site camera guidance pop up')
  }
}

const takePicture = () => {
  amplitude.track('Take a picture', { ...props.decodedData })
}

const onFileChange = async (e) => {
  let files = e.target.files || e.dataTransfer.files
  const picture = files[0]
  let reader = new FileReader()

  reader.onload = (e) => {
    previewSrc.value = e.target.result
  }

  reader.readAsDataURL(picture)
  amplitude.track('Confirm picture', { ...props.decodedData })

  const authResult = await Sentry.startSpan({name: "authentication request", "op": "st.auth", forceTransaction: true}, async () => {
    return await uploadPicture(picture)
  });

  await handleAuthResult(authResult)
}

const restartAuthFlow = () => {
  amplitude.track('Retake photo', { ...props.decodedData })
  fileInput.value.click()
  uploaded.value = false
  uploading.value = false
}

watch(() => events.value.triggerTakePicture, (newValue) => {
  if (newValue) {
    restartAuthFlow()
    events.value.triggerTakePicture = false
  }
})

const parseAuthProcessTime = (authResult) => {
  // Get tim returned by the auth process by parsing the debug_text field
  const parts = /timer?=([0-9.]+)/.exec(authResult.debug_text)
  if (!parts) {
    return null;
  }
  return Number(parts[1])
}

const uploadPicture = async (picture) => {
  if (uploading.value || !picture) return
  uploading.value = true

  Sentry.setMeasurement("st.auth.image.size", picture.size, "byte")

  let authRequestTime = performance.now()
  try {
    const authResult = await authenticationService.upload(picture, props.decodedData)
    authRequestTime = (performance.now() - authRequestTime) / 1000
    Sentry.setMeasurement("st.auth.time.request", authRequestTime, "second")

    if (authResult) {
      const authProcessTime = parseAuthProcessTime(authResult)
      if (authProcessTime) {
        Sentry.setMeasurement("st.auth.time.process", authProcessTime, "second")
        Sentry.setMeasurement("st.auth.time.network", authRequestTime - authProcessTime, "second")
      }
      const span = Sentry.getActiveSpan()
      if (span) {
        span.setAttribute("auth_status", authResult.authentication_status)
      }
    }

    return authResult
  } catch (error) {
    // in case of error log it and let the function end returning noting
    console.error(error)
  }
}

const handleAuthResult = async (authResult) => {
  if (authResult) {
    events.value.carrier_content = authResult.carrier_content
    authStatus.value = authResult.authentication_status
    imageQuality.value = authResult.image_quality
    const scan = authResult.scan

    // amplitude event
    const coreTechResult = JSON.parse(JSON.stringify(authResult))
    coreTechResult.version = __APP_VERSION__
    amplitude.track('Scan Result', coreTechResult)

    try {
      //redirect
      const redirectStatuses = ['auth_original', 'auth_copy', 'sid', 'inactive', 'blacklisted', 'not_trained']

      if (redirectStatuses.includes(authStatus.value) && scan.consumer_url) {
        if (!props.isStc) {
          await Sentry.close()
          uploading.value = false
          uploaded.value = true
          window.location.href = scan.consumer_url
        } else {
          window.parent.postMessage({ type: 'authentication-complete', scan: scan.uuid, url: scan.consumer_url }, '*')
        }
        setTimeout(() => {
          uploading.value = false
        }, 500)
      } else {
        uploading.value = false
        uploaded.value = true
      } // end redirect
    } catch (error) {
      console.error(error)
    }
  }
  uploading.value = false
  uploaded.value = true
}
</script>